import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Bug, Globe, Smartphone, Monitor, Shield, AlertTriangle, ArrowLeft, 
  Loader2, CheckCircle2, XCircle, Clock, FileText, Download, 
  ExternalLink, Copy, RefreshCw, ChevronDown, ChevronRight,
  Code, Target, Zap, BookOpen, Terminal, AlertOctagon,
  ShieldAlert, Wrench, Play, FileCode, Link2, Scale
} from 'lucide-react';
import { getProScan, startWebDASTScan, startMobileScan, downloadExecutiveVAPTReport, generateThreatModel, downloadThreatModelPDF, SEVERITY_COLORS, OWASP_TOP_10 } from '@/services/litesabrePro';
import api from '@/services/api';
import { toast } from 'sonner';

// Code Block Component with Syntax Highlighting
const CodeBlock = ({ code, language = 'text', highlightedLines = [], startLine = 1 }) => {
  const lines = code?.split('\n') || [];
  
  return (
    <div className="bg-gray-900 rounded-lg overflow-hidden text-sm font-mono">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <span className="text-gray-400 text-xs">{language}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 text-gray-400 hover:text-white"
          onClick={() => {
            navigator.clipboard.writeText(code);
            toast.success('Code copied');
          }}
        >
          <Copy className="w-3 h-3 mr-1" /> Copy
        </Button>
      </div>
      <ScrollArea className="max-h-64 w-full [&>[data-radix-scroll-area-viewport]>div]:!block">
        <pre className="p-3">
          {lines.map((line, idx) => {
            const lineNum = startLine + idx;
            const isHighlighted = highlightedLines.includes(lineNum);
            return (
              <div 
                key={`line-${lineNum}`} 
                className={`flex min-w-0 ${isHighlighted ? 'bg-red-900/40 -mx-3 px-3' : ''}`}
              >
                <span className={`w-8 shrink-0 text-right pr-3 select-none border-r border-gray-700 mr-3 ${isHighlighted ? 'text-red-400' : 'text-gray-500'}`}>
                  {lineNum}
                </span>
                <code className={`min-w-0 whitespace-pre-wrap break-all ${isHighlighted ? 'text-red-300' : 'text-gray-300'}`}>
                  {line || ' '}
                </code>
              </div>
            );
          })}
        </pre>
      </ScrollArea>
    </div>
  );
};

// CVSS Score Visual Component
const CVSSScore = ({ breakdown }) => {
  if (!breakdown) return null;
  
  const score = breakdown.score || 0;
  const getScoreColor = (s) => {
    if (s >= 9.0) return 'text-red-600 bg-red-100';
    if (s >= 7.0) return 'text-orange-600 bg-orange-100';
    if (s >= 4.0) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className={`text-3xl font-bold px-4 py-2 rounded-lg ${getScoreColor(score)}`}>
          {score.toFixed(1)}
        </div>
        <div>
          <p className="font-medium">CVSS v3.1 Score</p>
          <p className="text-xs text-gray-500 font-mono">{breakdown.vector}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="p-2 bg-gray-50 rounded">
          <span className="text-gray-500">Attack Vector:</span>
          <span className="ml-1 font-medium">{breakdown.attack_vector}</span>
        </div>
        <div className="p-2 bg-gray-50 rounded">
          <span className="text-gray-500">Complexity:</span>
          <span className="ml-1 font-medium">{breakdown.attack_complexity}</span>
        </div>
        <div className="p-2 bg-gray-50 rounded">
          <span className="text-gray-500">Privileges:</span>
          <span className="ml-1 font-medium">{breakdown.privileges_required}</span>
        </div>
        <div className="p-2 bg-gray-50 rounded">
          <span className="text-gray-500">User Interaction:</span>
          <span className="ml-1 font-medium">{breakdown.user_interaction}</span>
        </div>
      </div>
      <div className="flex gap-2 text-xs">
        <div className={`px-2 py-1 rounded ${breakdown.confidentiality_impact === 'High' ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
          C: {breakdown.confidentiality_impact}
        </div>
        <div className={`px-2 py-1 rounded ${breakdown.integrity_impact === 'High' ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
          I: {breakdown.integrity_impact}
        </div>
        <div className={`px-2 py-1 rounded ${breakdown.availability_impact === 'High' ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
          A: {breakdown.availability_impact}
        </div>
      </div>
    </div>
  );
};

// Exploitation Example Card
const ExploitationExample = ({ example, index }) => {
  const [isOpen, setIsOpen] = useState(index === 0);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 transition-colors">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-red-600" />
            <span className="font-medium text-red-900">{example.attack_name}</span>
          </div>
          {isOpen ? <ChevronDown className="w-4 h-4 text-red-600" /> : <ChevronRight className="w-4 h-4 text-red-600" />}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 p-4 bg-red-50/50 rounded-lg border border-red-100 space-y-3">
          <p className="text-sm text-gray-700">{example.description}</p>
          
          {example.payload_example && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Payload Example:</p>
              <code className="block text-sm bg-gray-900 text-red-400 p-2 rounded font-mono">
                {example.payload_example}
              </code>
            </div>
          )}
          
          {example.expected_result && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Expected Result:</p>
              <p className="text-sm text-red-700 bg-red-100 p-2 rounded">{example.expected_result}</p>
            </div>
          )}
          
          {example.real_world_cases?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Real-World Cases:</p>
              <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                {example.real_world_cases.map((c, i) => (
                  <li key={`case-${i}-${c.slice(0,15)}`}>{c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Remediation Step Card
const RemediationStep = ({ step }) => {
  const [showCode, setShowCode] = useState(false);
  
  return (
    <div className="border-l-4 border-green-500 pl-4 py-2">
      <div className="flex items-start gap-3">
        <div className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
          {step.step_number}
        </div>
        <div className="flex-1 space-y-2">
          <h5 className="font-medium text-gray-900">{step.title}</h5>
          <p className="text-sm text-gray-600">{step.description}</p>
          
          {(step.code_before || step.code_after) && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowCode(!showCode)}
              className="mt-2"
            >
              <Code className="w-3 h-3 mr-1" />
              {showCode ? 'Hide Code' : 'Show Code Example'}
            </Button>
          )}
          
          {showCode && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              {step.code_before && (
                <div>
                  <p className="text-xs font-medium text-red-600 mb-1 flex items-center gap-1">
                    <XCircle className="w-3 h-3" /> Vulnerable Code
                  </p>
                  <CodeBlock code={step.code_before} language={step.code_language} />
                </div>
              )}
              {step.code_after && (
                <div>
                  <p className="text-xs font-medium text-green-600 mb-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Secure Code
                  </p>
                  <CodeBlock code={step.code_after} language={step.code_language} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Reproduction Step Card
const ReproductionStep = ({ step }) => {
  return (
    <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
        {step.step_number}
      </div>
      <div className="flex-1 space-y-2">
        <p className="text-sm font-medium text-gray-900">{step.action}</p>
        <p className="text-xs text-gray-500">Expected: {step.expected_result}</p>
        
        {step.curl_command && (
          <div className="mt-2">
            <p className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
              <Terminal className="w-3 h-3" /> cURL Command
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 text-xs bg-gray-900 text-green-400 p-2 rounded font-mono whitespace-pre-wrap break-all">
                {step.curl_command}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(step.curl_command);
                  toast.success('Command copied');
                }}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Reproducible Request Card — one-click "Copy as curl" / raw HTTP export for testers
const buildPrimaryCurl = (finding) => {
  const curls = (finding.reproduction_steps || []).map((s) => s.curl_command).filter(Boolean);
  let primary = null;
  if (finding.payload) primary = curls.find((c) => c.includes(finding.payload)) || null;
  // For header/cookie findings the specific `| grep -i '<Header>'` command is the most useful to replay.
  if (!primary) primary = curls.find((c) => c.includes('grep -i')) || null;
  if (!primary) primary = curls.find((c) => /^curl\s+-i/.test(c)) || curls[0] || null;
  if (!primary) {
    const url = finding.url || finding.endpoint;
    if (url) {
      const method = (finding.method || 'GET').toUpperCase();
      primary = `curl -i -X ${method} '${url}'`;
    }
  }
  return primary;
};

const ReproduceCard = ({ finding }) => {
  const curl = buildPrimaryCurl(finding);
  const rawRequest = finding.http_request_raw;
  if (!curl && !rawRequest) return null;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  return (
    <Card className="border-emerald-200 bg-emerald-50/40" data-testid="reproduce-request-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-emerald-800">
          <Terminal className="w-4 h-4" /> Reproducible Request
        </CardTitle>
        <CardDescription>Replay this exact finding in one click</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {curl && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                <Terminal className="w-3 h-3" /> cURL
              </p>
              <Button
                size="sm"
                variant="outline"
                className="h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid="copy-as-curl-btn"
                onClick={() => copy(curl, 'Copied as curl')}
              >
                <Copy className="w-3 h-3 mr-1" /> Copy as curl
              </Button>
            </div>
            <code className="block text-xs bg-gray-900 text-green-400 p-2 rounded font-mono whitespace-pre-wrap break-all">
              {curl}
            </code>
          </div>
        )}
        {rawRequest && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500">Raw HTTP request</p>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-gray-500 hover:text-gray-900"
                data-testid="copy-raw-request-btn"
                onClick={() => copy(rawRequest, 'Raw request copied')}
              >
                <Copy className="w-3 h-3 mr-1" /> Copy raw
              </Button>
            </div>
            <code className="block text-xs bg-gray-900 text-gray-300 p-2 rounded font-mono whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
              {rawRequest}
            </code>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Finding Card Component
const FindingCard = ({ finding, isSelected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-600',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-blue-500',
      info: 'bg-gray-400'
    };
    return colors[severity?.toLowerCase()] || colors.info;
  };
  
  const getSeverityBorder = (severity) => {
    const colors = {
      critical: 'border-l-red-600',
      high: 'border-l-orange-500',
      medium: 'border-l-yellow-500',
      low: 'border-l-blue-500',
      info: 'border-l-gray-400'
    };
    return colors[severity?.toLowerCase()] || colors.info;
  };

  return (
    <Card 
      className={`border-l-4 ${getSeverityBorder(finding.severity)} cursor-pointer transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}
      onClick={() => onSelect(finding)}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`${getSeverityColor(finding.severity)} text-white`}>
              {finding.severity?.toUpperCase()}
            </Badge>
            {finding.cvss_score && (
              <Badge variant="outline" className="font-mono">
                CVSS: {finding.cvss_score}
              </Badge>
            )}
            {finding.owasp_id && (
              <Badge variant="outline" className="bg-purple-50">
                {finding.owasp_id}
              </Badge>
            )}
            {finding.cwe_id && (
              <Badge variant="outline" className="bg-blue-50">
                {finding.cwe_id}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Title & Description */}
        <h3 className="font-semibold text-gray-900 mb-2">{finding.title}</h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{finding.description}</p>
        
        {/* URL & Parameter */}
        {finding.url && (
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <Globe className="w-3 h-3" />
            <code className="bg-gray-100 px-2 py-0.5 rounded truncate max-w-[300px]">{finding.url}</code>
            {finding.parameter && (
              <>
                <span className="text-gray-400">|</span>
                <span className="font-medium">Param:</span>
                <code className="bg-yellow-100 px-2 py-0.5 rounded">{finding.parameter}</code>
              </>
            )}
          </div>
        )}
        
        {/* Quick Stats */}
        <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 border-t">
          {finding.exploitation_examples?.length > 0 && (
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-red-500" />
              {finding.exploitation_examples.length} exploit examples
            </span>
          )}
          {finding.remediation_steps?.length > 0 && (
            <span className="flex items-center gap-1">
              <Wrench className="w-3 h-3 text-green-500" />
              {finding.remediation_steps.length} fix steps
            </span>
          )}
          {finding.reproduction_steps?.length > 0 && (
            <span className="flex items-center gap-1">
              <Play className="w-3 h-3 text-blue-500" />
              {finding.reproduction_steps.length} repro steps
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Detailed Finding View
const FindingDetailView = ({ finding, scanId, onEnriched }) => {
  const [explaining, setExplaining] = React.useState(false);
  const [aiResult, setAiResult] = React.useState(null);

  React.useEffect(() => {
    // Reset per-finding when switching
    setAiResult(finding?.ai_business_impact ? {
      business_impact: finding.ai_business_impact,
      attack_scenario: finding.ai_attack_scenario,
      proof_of_concept: finding.ai_proof_of_concept,
      remediation_summary: finding.ai_remediation_summary,
      provider: finding.ai_provider,
      model: finding.ai_model,
    } : null);
  }, [finding?.id]);

  const handleExplain = async () => {
    if (!finding?.id || !scanId) return;
    try {
      setExplaining(true);
      const res = await api.post(
        `/litesabre-pro/scans/${scanId}/findings/${finding.id}/explain-ai`,
        {}
      );
      setAiResult(res.data);
      toast.success(`AI explained via ${res.data.provider} (${res.data.model})`);
      onEnriched?.(finding.id, res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'AI enrichment failed');
    } finally {
      setExplaining(false);
    }
  };

  if (!finding) {
    return (
      <div className="flex flex-col items-center justify-center h-[600px] text-gray-400">
        <Bug className="w-12 h-12 mb-3" />
        <p>Select a finding to view details</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-200px)] w-full [&>[data-radix-scroll-area-viewport]>div]:!block">
      <div className="space-y-6 pr-4 min-w-0 max-w-full">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge className={`${finding.severity === 'critical' ? 'bg-red-600' : finding.severity === 'high' ? 'bg-orange-500' : finding.severity === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'} text-white`}>
              {finding.severity?.toUpperCase()}
            </Badge>
            {finding.owasp_id && <Badge variant="outline">{finding.owasp_id}</Badge>}
            {finding.cwe_id && (
              <a 
                href={`https://cwe.mitre.org/data/definitions/${finding.cwe_id.replace('CWE-', '')}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
              >
                {finding.cwe_id} <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          <h2 className="text-xl font-bold text-gray-900">{finding.title}</h2>
          <p className="text-gray-600 mt-2">{finding.description}</p>
          <div className="mt-3">
            <Button
              size="sm"
              onClick={handleExplain}
              disabled={explaining}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
              data-testid="explain-with-ai-btn"
            >
              {explaining ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              {aiResult ? 'Re-explain with AI' : 'Explain with AI'}
            </Button>
            {aiResult?.provider && (
              <span className="ml-3 text-xs text-gray-500">
                via {aiResult.provider} · {aiResult.model}
              </span>
            )}
          </div>
        </div>

        <ReproduceCard finding={finding} />

        {aiResult && (
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50"
                data-testid="ai-explanation-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-purple-900">
                <Zap className="w-4 h-4" /> AI Enrichment
              </CardTitle>
              <CardDescription>Business impact + PoC drafted by AI</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {aiResult.business_impact && (
                <div>
                  <div className="text-xs font-bold uppercase text-purple-800 mb-1">Business Impact</div>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{aiResult.business_impact}</p>
                </div>
              )}
              {aiResult.attack_scenario && (
                <div>
                  <div className="text-xs font-bold uppercase text-purple-800 mb-1">Attack Scenario</div>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{aiResult.attack_scenario}</p>
                </div>
              )}
              {aiResult.proof_of_concept && (
                <div>
                  <div className="text-xs font-bold uppercase text-purple-800 mb-1">Proof of Concept</div>
                  <pre className="bg-gray-900 text-gray-100 text-[11px] p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono">
                    {aiResult.proof_of_concept}
                  </pre>
                </div>
              )}
              {aiResult.remediation_summary && (
                <div>
                  <div className="text-xs font-bold uppercase text-purple-800 mb-1">Remediation Summary</div>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{aiResult.remediation_summary}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CVSS Score */}
        {finding.cvss_breakdown && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Scale className="w-4 h-4" /> CVSS Score Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CVSSScore breakdown={finding.cvss_breakdown} />
            </CardContent>
          </Card>
        )}

        {/* Business Impact */}
        {finding.business_impact && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
                <AlertOctagon className="w-4 h-4" /> Business Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-900">{finding.business_impact}</p>
            </CardContent>
          </Card>
        )}

        {/* Evidence / Response Data */}
        {finding.evidence && (
          <Card className="border-red-200 bg-red-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                <ShieldAlert className="w-4 h-4" /> Evidence
              </CardTitle>
              <CardDescription>Detected sensitive data in server response</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {finding.evidence.type && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Data Type:</span>
                  <Badge variant="destructive">{finding.evidence.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                </div>
              )}
              {finding.evidence.count > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Matches Found:</span>
                  <Badge variant="outline" className="font-mono">{finding.evidence.count}</Badge>
                </div>
              )}
              {finding.evidence.sample_matches?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Matched Samples (masked):</p>
                  <div className="flex flex-wrap gap-2">
                    {finding.evidence.sample_matches.map((m, i) => (
                      <code key={`evidence-match-${i}`} className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded font-mono border border-red-200">{m}</code>
                    ))}
                  </div>
                </div>
              )}
              {typeof finding.evidence === 'string' && (
                <div className="bg-gray-900 rounded-lg p-3 overflow-x-auto">
                  <code className="text-xs text-red-400 font-mono whitespace-pre-wrap">{finding.evidence}</code>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Vulnerable Code */}
        {finding.vulnerability_code && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Code className="w-4 h-4 text-red-600" /> Vulnerable Code Pattern
              </CardTitle>
              {finding.vulnerability_code.file_path && (
                <CardDescription className="font-mono text-xs">
                  {finding.vulnerability_code.file_path}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <CodeBlock 
                code={finding.vulnerability_code.code}
                language={finding.vulnerability_code.language}
                highlightedLines={finding.vulnerability_code.highlighted_lines}
                startLine={finding.vulnerability_code.start_line}
              />
            </CardContent>
          </Card>
        )}

        {/* Attack Scenario */}
        {finding.attack_scenario && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-red-600" /> Attack Scenario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-700">{finding.attack_scenario}</p>
            </CardContent>
          </Card>
        )}

        {/* Exploitation Examples */}
        {finding.exploitation_examples?.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                <Zap className="w-4 h-4" /> Exploitation Examples
              </CardTitle>
              <CardDescription>Real-world attack techniques and historical cases</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {finding.exploitation_examples.map((example, idx) => (
                <ExploitationExample key={`exploit-${idx}`} example={example} index={idx} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Steps to Reproduce */}
        {finding.reproduction_steps?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Play className="w-4 h-4 text-blue-600" /> Steps to Reproduce
              </CardTitle>
              <CardDescription>Follow these steps to verify the vulnerability</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {finding.reproduction_steps.map((step, idx) => (
                <ReproductionStep key={`repro-${idx}`} step={step} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Remediation Steps */}
        {finding.remediation_steps?.length > 0 && (
          <Card className="border-green-200 bg-green-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-green-700">
                <Wrench className="w-4 h-4" /> Remediation Steps
              </CardTitle>
              <CardDescription>Follow these steps to fix the vulnerability</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {finding.remediation_steps.map((step, idx) => (
                <RemediationStep key={`remed-${idx}`} step={step} />
              ))}
            </CardContent>
          </Card>
        )}

        {/* Compliance Mapping */}
        {finding.compliance_frameworks && Object.keys(finding.compliance_frameworks).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-600" /> Compliance Framework Mapping
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(finding.compliance_frameworks).map(([framework, controls]) => (
                  <div key={framework} className="p-3 bg-purple-50 rounded-lg">
                    <p className="font-medium text-purple-900 text-sm mb-1">{framework}</p>
                    <div className="flex flex-wrap gap-1">
                      {controls.map((control, idx) => (
                        <Badge key={`ctrl-${control}`} variant="outline" className="text-xs bg-white">
                          {control}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* References */}
        {finding.references?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> References
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1">
                {finding.references.map((ref, idx) => (
                  <li key={`ref-${ref.slice(0, 30)}`}>
                    <a 
                      href={ref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {ref.length > 60 ? ref.substring(0, 60) + '...' : ref}
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </ScrollArea>
  );
};

// Main Page Component
const LiteSabreProScanDetailPage = () => {
  const { scanId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const proBasePath = location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre-pro' 
    : '/grc/litesabre-pro';
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [retesting, setRetesting] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('all');
  // Threat model dialog state
  const [showThreatModel, setShowThreatModel] = useState(false);
  const [threatModel, setThreatModel] = useState(null);
  const [threatModelLoading, setThreatModelLoading] = useState(false);

  const openThreatModel = async (regenerate = false) => {
    try {
      setThreatModelLoading(true);
      const model = await generateThreatModel(scanId, { regenerate });
      setThreatModel(model);
      setShowThreatModel(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to generate threat model');
    } finally {
      setThreatModelLoading(false);
    }
  };

  const handleRetest = async () => {
    if (!scan) return;
    try {
      setRetesting(true);
      // Server-side /retest handles auth/target/profile reuse AND links
      // parent_scan_id so the executive report can mark resolved findings.
      const res = await api.post(`/litesabre-pro/scans/${scanId}/retest`);
      const newId = res.data?.new_scan_id;
      toast.success('Retest started');
      if (newId) {
        navigate(`${proBasePath}/scans/${newId}`);
      } else {
        await loadScan();
      }
    } catch (error) {
      console.error('Retest failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to start retest');
    } finally {
      setRetesting(false);
    }
  };

  const loadScan = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getProScan(scanId);
      setScan(data);
      if (data.findings?.length > 0) {
        setSelectedFinding(data.findings[0]);
      }
    } catch (error) {
      console.error('Failed to load scan:', error);
      toast.error('Failed to load scan details');
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => {
    loadScan();
  }, [loadScan]);

  const downloadReport = async (format) => {
    try {
      setDownloading(true);
      const response = await api.get(`/litesabre-pro/scans/${scanId}/report`, {
        params: { format },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'html' ? 'text/html' : format === 'pdf' ? 'application/pdf' : 'text/plain' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `litesabre_report_${scanId}.${format === 'html' ? 'html' : format === 'pdf' ? 'pdf' : 'txt'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Report downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to download report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running': return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default: return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getScanTypeIcon = (type) => {
    if (type?.includes('web')) return <Globe className="w-5 h-5 text-blue-500" />;
    if (type?.includes('mobile')) return <Smartphone className="w-5 h-5 text-green-500" />;
    if (type?.includes('desktop')) return <Monitor className="w-5 h-5 text-purple-500" />;
    return <Bug className="w-5 h-5 text-red-500" />;
  };

  const filteredFindings = scan?.findings?.filter(f => {
    if (severityFilter === 'all') return true;
    return f.severity?.toLowerCase() === severityFilter;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={() => navigate(`${proBasePath}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Scanner
        </Button>
        <div className="text-center py-12">
          <Bug className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600">Scan Not Found</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${proBasePath}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              {getScanTypeIcon(scan.scan_type)}
              {scan.name || 'Scan Details'}
            </h1>
            <p className="text-gray-500 flex items-center gap-2">
              {getStatusIcon(scan.status)}
              <span className="capitalize">{scan.status}</span>
              <span className="text-gray-400">•</span>
              {new Date(scan.created_at).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadScan}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleRetest}
            disabled={retesting || scan.status === 'running'}
            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            data-testid="retest-btn"
          >
            {retesting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Retest
          </Button>
          <Button 
            onClick={() => downloadReport('pdf')} 
            disabled={downloading || scan.status !== 'completed'}
            className="bg-red-600 hover:bg-red-700"
            data-testid="export-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export PDF
          </Button>
          <Button
            onClick={async () => {
              try {
                setDownloading(true);
                await downloadExecutiveVAPTReport(scanId, scan.name || 'scan');
                toast.success('Executive VAPT report downloaded');
              } catch (err) {
                console.error(err);
                toast.error('Failed to generate executive report');
              } finally {
                setDownloading(false);
              }
            }}
            disabled={downloading || scan.status !== 'completed'}
            className="bg-indigo-900 hover:bg-indigo-950 text-white"
            data-testid="export-exec-vapt-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Executive Report
          </Button>
          <Button
            onClick={() => openThreatModel(false)}
            disabled={threatModelLoading || scan.status !== 'completed'}
            className="bg-amber-800 hover:bg-amber-900 text-white"
            data-testid="threat-model-btn"
          >
            {threatModelLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Shield className="w-4 h-4 mr-2" />
            )}
            Threat Model
          </Button>
          <Button 
            variant="outline"
            onClick={() => downloadReport('html')} 
            disabled={downloading || scan.status !== 'completed'}
            data-testid="export-html-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            HTML Report
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 text-white">
          <CardContent className="p-4">
            <p className="text-gray-300 text-sm">Total Findings</p>
            <p className="text-3xl font-bold">{scan.findings?.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-600">
          <CardContent className="p-4">
            <p className="text-red-600 text-sm font-medium">Critical</p>
            <p className="text-2xl font-bold text-red-600">{scan.summary?.critical_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <p className="text-orange-600 text-sm font-medium">High</p>
            <p className="text-2xl font-bold text-orange-500">{scan.summary?.high_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-4">
            <p className="text-yellow-600 text-sm font-medium">Medium</p>
            <p className="text-2xl font-bold text-yellow-500">{scan.summary?.medium_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-blue-600 text-sm font-medium">Low</p>
            <p className="text-2xl font-bold text-blue-500">{scan.summary?.low_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Findings List */}
        <div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Security Findings
                </CardTitle>
                <div className="flex gap-1">
                  {['all', 'critical', 'high', 'medium', 'low'].map(sev => (
                    <Button
                      key={sev}
                      variant={severityFilter === sev ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSeverityFilter(sev)}
                      className={severityFilter === sev ? '' : 'text-xs'}
                    >
                      {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>
              <CardDescription>
                {filteredFindings.length} findings {severityFilter !== 'all' ? `(${severityFilter})` : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-3 pr-4">
                  {filteredFindings.length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                      <p className="text-gray-500">No findings match the filter</p>
                    </div>
                  ) : (
                    filteredFindings.map((finding, index) => (
                      <FindingCard
                        key={finding.id || index}
                        finding={finding}
                        isSelected={selectedFinding?.id === finding.id}
                        onSelect={setSelectedFinding}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Finding Detail */}
        <div>
          <Card className="sticky top-6">
            <CardHeader className="border-b">
              <CardTitle className="flex items-center gap-2">
                <FileCode className="w-5 h-5 text-indigo-500" />
                Finding Details
              </CardTitle>
              <CardDescription>
                Detailed analysis, exploitation examples, and remediation
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <FindingDetailView
                finding={selectedFinding}
                scanId={scanId}
                onEnriched={(fid, data) => {
                  // Update local copy so persisted enrichment shows on next select
                  setScan((prev) => prev ? {
                    ...prev,
                    findings: (prev.findings || []).map((f) =>
                      f.id === fid ? { ...f,
                        ai_business_impact: data.business_impact,
                        ai_attack_scenario: data.attack_scenario,
                        ai_proof_of_concept: data.proof_of_concept,
                        ai_remediation_summary: data.remediation_summary,
                        ai_provider: data.provider,
                        ai_model: data.model,
                      } : f
                    )
                  } : prev);
                }}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Threat Model Dialog */}
      <Dialog open={showThreatModel} onOpenChange={setShowThreatModel}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="threat-model-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-700" />
              STRIDE Threat Model
            </DialogTitle>
            <DialogDescription>
              Auto-generated from the scan flow, discovered endpoints, and DAST findings.
            </DialogDescription>
          </DialogHeader>
          {threatModel && (
            <div className="space-y-6">
              {/* STRIDE summary grid */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">
                  STRIDE Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(threatModel.stride_summary || {}).map(([cat, cnt]) => (
                    <div key={cat} className="border rounded-lg p-3 bg-white flex items-center justify-between">
                      <span className="text-sm">{cat}</span>
                      <Badge variant="outline" className="bg-amber-50 border-amber-200">{cnt}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assets */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">
                  Assets ({threatModel.assets?.length || 0})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {(threatModel.assets || []).map((a) => (
                    <div key={a.id} className="border rounded p-2 bg-gray-50">
                      <div className="text-xs font-bold">{a.name}</div>
                      <div className="text-[10px] text-gray-500">{a.kind} · {a.exposure}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Threats by STRIDE */}
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">
                  Threats
                </h3>
                {['Spoofing','Tampering','Repudiation','Information Disclosure','Denial of Service','Elevation of Privilege'].map((stride) => {
                  const list = (threatModel.threats || []).filter((t) => t.stride === stride);
                  if (!list.length) return null;
                  return (
                    <div key={stride} className="mb-3">
                      <div className="text-xs font-semibold text-amber-800 mb-1">
                        {stride} <span className="text-gray-500">({list.length})</span>
                      </div>
                      <div className="space-y-1 max-h-40 overflow-y-auto pl-2 border-l-2 border-amber-200">
                        {list.slice(0, 12).map((t) => (
                          <div key={t.id} className="text-xs">
                            <span className="text-gray-500 font-mono mr-2">{t.asset}</span>
                            <span className="text-gray-800">{t.description}</span>
                            <span className="ml-2 text-[10px] text-gray-500">
                              [L:{t.likelihood} · I:{t.impact} · {t.source}]
                            </span>
                          </div>
                        ))}
                        {list.length > 12 && (
                          <div className="text-[10px] text-gray-400 italic">
                            + {list.length - 12} more (see PDF)
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-2 justify-end border-t pt-3">
                <Button variant="outline" onClick={() => openThreatModel(true)}
                        disabled={threatModelLoading} data-testid="regenerate-threat-model-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Regenerate
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await downloadThreatModelPDF(scanId, scan.name || 'scan');
                      toast.success('Threat model PDF downloaded');
                    } catch (err) {
                      toast.error('Failed to download threat model PDF');
                    }
                  }}
                  className="bg-amber-800 hover:bg-amber-900 text-white"
                  data-testid="download-threat-model-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiteSabreProScanDetailPage;
